import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    TextField,
    Stack,
    Tabs,
    Tab,
    Alert,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { Close as CloseIcon, HowToReg as IdentifyIcon, HelpOutline as HelpIcon } from '@mui/icons-material';

export const IDENTIFY_CLIENT_V2_NATIVE_OPTIONS = {
    notClient: 'Ainda não sou Cliente',
    atendente: 'Falar com um atendente',
};

export const IDENTIFY_CLIENT_V2_DEFAULTS = {
    maxCpfAttempts: 3,
    ativarSaidaAtendente: false,
    askCpfMessage: 'Para continuar o atendimento, informe seu *CPF* ou *CNPJ* ou digite uma das opções abaixo:',
    cpfNotFoundMessage: '❌*Não encontramos cadastro com este CPF/CNPJ*. Verifique e tente novamente, ou escolha uma opção:',
    confirmIdentityMessage: 'Encontramos o cadastro: *{{nome_cliente}}*\nEste e o seu cadastro?\n1️⃣ - Sim\n2️⃣ - Nao',
    identityDeniedMessage: 'Entendido.\n1️⃣ - Informar CPF/CNPJ novamente\n2️⃣ - Aguardar atendimento',
    askLinkConsentMessage: 'Podemos utilizar este CPF/CNPJ para identifica-lo automaticamente nas proximas vezes que entrar em contato?\n1️⃣ - Sim\n2️⃣ - Nao',
};

const THEME = '#004d40';
const THEME_HOVER = '#00332c';

interface FlowBuilderIdentifyClientV2ModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: Record<string, any>) => void;
    data?: Record<string, any>;
}

const FlowBuilderIdentifyClientV2Modal = ({
    open,
    onClose,
    onSave,
    data,
}: FlowBuilderIdentifyClientV2ModalProps) => {
    const [activeTab, setActiveTab] = useState(0);
    const [helpOpen, setHelpOpen] = useState(false);
    const [form, setForm] = useState<Record<string, any>>({ ...IDENTIFY_CLIENT_V2_DEFAULTS, label: 'Identificar Cliente V2' });

    useEffect(() => {
        if (open) {
            setForm({ ...IDENTIFY_CLIENT_V2_DEFAULTS, label: 'Identificar Cliente V2', ...data });
            setActiveTab(0);
        }
    }, [open, data]);

    const update = (patch: Record<string, any>) => {
        setForm((prev) => ({ ...prev, ...patch }));
    };

    const handleSave = () => {
        onSave(form);
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IdentifyIcon sx={{ color: THEME }} />
                        <Typography variant="h6">Identificar Cliente V2</Typography>
                        <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ color: THEME }}>
                            <HelpIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        <Alert severity="info">
                            Saidas: Cliente · Cliente Inativo · Suspenso · Nao cliente (opcao 1){form.ativarSaidaAtendente ? ' · Falar com atendente (opcao 2)' : ''}
                        </Alert>

                        <TextField
                            label="Nome do no"
                            fullWidth
                            size="small"
                            value={form.label || ''}
                            onChange={(e) => update({ label: e.target.value })}
                        />

                        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tab label="Configuracao" />
                            <Tab label="Mensagens" />
                        </Tabs>

                        {activeTab === 0 && (
                            <Stack spacing={2}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!form.ativarSaidaAtendente}
                                            onChange={(e) => update({ ativarSaidaAtendente: e.target.checked })}
                                            sx={{ color: THEME, '&.Mui-checked': { color: THEME } }}
                                        />
                                    }
                                    label="Ativar falar com atendente (adiciona opcao 2 nativa do no, como um menu)"
                                />
                                <TextField
                                    label="Maximo de tentativas de CPF/CNPJ"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={form.maxCpfAttempts ?? 3}
                                    onChange={(e) => update({ maxCpfAttempts: Math.max(1, parseInt(e.target.value, 10) || 3) })}
                                    inputProps={{ min: 1, max: 10 }}
                                    helperText="Apos esgotar as tentativas, o fluxo segue pela saida Nao cliente."
                                />
                            </Stack>
                        )}

                        {activeTab === 1 && (
                            <Stack spacing={2}>
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                    As opcoes do menu sao nativas do no (como no Menu):<br />
                                    1️⃣ {IDENTIFY_CLIENT_V2_NATIVE_OPTIONS.notClient}
                                    {form.ativarSaidaAtendente ? (
                                        <>
                                            <br />
                                            2️⃣ {IDENTIFY_CLIENT_V2_NATIVE_OPTIONS.atendente}
                                        </>
                                    ) : null}
                                </Alert>
                                <TextField
                                    label="Mensagem inicial (CPF/CNPJ)"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    value={form.askCpfMessage || ''}
                                    onChange={(e) => update({ askCpfMessage: e.target.value })}
                                    helperText="Nao inclua as opcoes 1 e 2 aqui — elas sao adicionadas automaticamente pelo no."
                                />
                                <TextField
                                    label="CPF/CNPJ nao encontrado"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    size="small"
                                    value={form.cpfNotFoundMessage || ''}
                                    onChange={(e) => update({ cpfNotFoundMessage: e.target.value })}
                                    helperText="Mesmas opcoes nativas serao anexadas automaticamente."
                                />
                                <TextField label="Confirmar identidade" fullWidth multiline rows={3} size="small" value={form.confirmIdentityMessage || ''} onChange={(e) => update({ confirmIdentityMessage: e.target.value })} helperText="Use {{nome_cliente}}" />
                                <TextField label="Identidade negada" fullWidth multiline rows={2} size="small" value={form.identityDeniedMessage || ''} onChange={(e) => update({ identityDeniedMessage: e.target.value })} />
                                <TextField label="Consentimento de vinculo" fullWidth multiline rows={3} size="small" value={form.askLinkConsentMessage || ''} onChange={(e) => update({ askLinkConsentMessage: e.target.value })} helperText="1️⃣ Sim vincula o numero ao cliente permanentemente. 2️⃣ Nao usa so neste atendimento." />
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} color="inherit">Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: THEME, '&:hover': { bgcolor: THEME_HOVER } }}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Como funciona o no Identificar Cliente V2</Typography>
                    <IconButton size="small" onClick={() => setHelpOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        <Typography variant="body2" color="text.secondary">
                            Identifica o cliente por CPF/CNPJ quando o numero ainda nao esta vinculado, com opcoes claras para quem nao e cliente ou quer falar com atendente.
                        </Typography>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Mensagem inicial (exemplo)</Typography>
                            <Typography component="div" variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1, whiteSpace: 'pre-line' }}>
                                {IDENTIFY_CLIENT_V2_DEFAULTS.askCpfMessage}
                                {'\n1️⃣ - '}{IDENTIFY_CLIENT_V2_NATIVE_OPTIONS.notClient}
                                {'\n2️⃣ - '}{IDENTIFY_CLIENT_V2_NATIVE_OPTIONS.atendente}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                A opcao 1 e sempre nativa do no. A opcao 2 so aparece quando &quot;Ativar falar com atendente&quot; estiver marcada.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Fluxo</Typography>
                            <Typography variant="body2" component="div" color="text.secondary">
                                <ol style={{ margin: 0, paddingLeft: 20 }}>
                                    <li>Se ja vinculado → saida conforme o <strong>status</strong> do cliente (Cliente, Cliente Inativo ou Suspenso).</li>
                                    <li>Se nao vinculado → envia a mensagem com CPF/CNPJ e opcoes.</li>
                                    <li><strong>1</strong> → saida <strong>Nao cliente</strong>.</li>
                                    <li><strong>2</strong> → saida <strong>Falar com atendente</strong> (somente se habilitada nas configuracoes).</li>
                                    <li>CPF/CNPJ valido → confirma identidade → pergunta vinculo → saida conforme o status do cliente.</li>
                                </ol>
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Saidas</Typography>
                            <Typography variant="body2" component="div" color="text.secondary">
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li><strong>Cliente:</strong> vinculado ou identificado com status ativo (ou outro status que nao seja inativo/suspenso).</li>
                                    <li><strong>Cliente Inativo:</strong> cliente identificado com status inativo.</li>
                                    <li><strong>Suspenso:</strong> cliente identificado com status suspenso.</li>
                                    <li><strong>Nao cliente:</strong> opcao 1, CPF nao encontrado apos tentativas ou aguardar atendimento.</li>
                                    <li><strong>Falar com atendente:</strong> opcao 2 (se habilitada) — conecte a Alterar Fila ou Alterar Usuario.</li>
                                </ul>
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setHelpOpen(false)} variant="contained" sx={{ bgcolor: THEME, '&:hover': { bgcolor: THEME_HOVER } }}>
                        Entendi
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FlowBuilderIdentifyClientV2Modal;
